import type {
  IFilterOptions,
  IOptions,
  IOptionsProps,
} from "@fluidattacks/design";
import {
  AppliedFilters,
  Button,
  Container,
  Heading,
  Table,
  formatCheckValues,
  useFilters,
} from "@fluidattacks/design";
import type { ColumnDef } from "@tanstack/react-table";
import capitalize from "lodash/capitalize";
import { useCallback, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import type {
  IOrganizationActorAttr,
  IOrganizationAuthorAttr,
  IOrganizationAuthorsTable,
} from "../types";
import { useTable } from "hooks/use-table";
import { useFiltersStorage } from "storage/filters";

interface IOrganizationAuthorAttrsProps {
  readonly authors: IOrganizationAuthorAttr[];
}

export const OrganizationAuthors: FC<IOrganizationAuthorAttrsProps> = ({
  authors,
}): JSX.Element => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<IOrganizationAuthorsTable[]>([]);
  const formatActor = (actor: string): IOrganizationActorAttr | undefined => {
    const textMatch = /^(?<name>.+) <(?<email>[^>]+)>$/u.exec(actor);
    if (textMatch?.groups === undefined) return undefined;

    const { email, name } = textMatch.groups;

    return { email, name };
  };

  const formatAuthorsData = useCallback(
    (authorData: IOrganizationAuthorAttr[]): IOrganizationAuthorsTable[] =>
      authorData.map((author): IOrganizationAuthorsTable => {
        const actor = formatActor(author.actor);
        const actorName = capitalize(actor?.name);
        const actorEmail = actor?.email;
        const groupsAuthors = author.activeGroups
          .map((group): string => group.name)
          .join(", ");

        return { actorEmail, actorName, groupsAuthors, id: actorName };
      }),
    [],
  );

  const tableColumns: ColumnDef<IOrganizationAuthorsTable>[] = [
    {
      accessorKey: "actorName",
      header: t("organization.tabs.billing.authors.headers.authorName"),
      size: 350,
    },
    {
      accessorKey: "actorEmail",
      header: t("organization.tabs.billing.authors.headers.authorEmail"),
      size: 350,
    },
    {
      accessorKey: "groupsAuthors",
      header: t("organization.tabs.billing.authors.headers.activeGroups"),
      size: 600,
    },
  ];

  const dataset = useMemo(
    (): IOrganizationAuthorsTable[] => formatAuthorsData(authors),
    [authors, formatAuthorsData],
  );

  const uniqueGroupsAuthors = useMemo(
    (): string[] =>
      Array.from(
        new Set(
          dataset.flatMap(({ groupsAuthors }): string[] =>
            groupsAuthors.split(", "),
          ),
        ),
      ),
    [dataset],
  );

  const optionsFilters = useMemo(
    (): IOptionsProps<IOrganizationAuthorsTable>[] => [
      {
        filterOptions: [
          {
            key: "actorName",
            label: t("organization.tabs.billing.authors.headers.authorName"),
            type: "text",
          },
        ],
        label: t("organization.tabs.billing.authors.headers.authorName"),
      },
      {
        filterOptions: [
          {
            key: "actorEmail",
            label: t("organization.tabs.billing.authors.headers.authorEmail"),
            type: "text",
          },
        ],
        label: t("organization.tabs.billing.authors.headers.authorEmail"),
      },
      {
        filterOptions: [
          {
            key: "groupsAuthors",
            label: t("organization.tabs.billing.authors.filters.activeGroups"),
            options: uniqueGroupsAuthors.map(
              (option): IOptions => ({ label: option, value: option }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("organization.tabs.billing.authors.headers.activeGroups"),
      },
    ],
    [uniqueGroupsAuthors, t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("billing_authors") as string | null;
  }, [getValues]);

  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("billing_authors", value);
    },
    [setValues],
  );

  const getFilter = (
    getFilters: readonly IFilterOptions<IOrganizationAuthorsTable>[],
    key: string,
  ): IFilterOptions<IOrganizationAuthorsTable> | undefined =>
    getFilters.find((filter): boolean => filter.key === key);

  const handleFilterChange = useCallback(
    (
      newFilters: readonly IFilterOptions<IOrganizationAuthorsTable>[],
    ): void => {
      const groupsAuthors = formatCheckValues(
        getFilter(newFilters, "groupsAuthors")?.options,
      )
        ?.split(",")
        .map((value): string => value.trim().toLowerCase());

      setFilters(
        dataset.filter((item): boolean =>
          newFilters.every((filter): boolean => {
            const filterValue = filter.value?.toString().toLowerCase() ?? "";
            const itemValue =
              item[filter.key as keyof IOrganizationAuthorsTable]
                ?.toString()
                .toLowerCase() ?? "";

            if (filter.type === "checkboxes") {
              if (!groupsAuthors || groupsAuthors.length === 0) return true;

              const itemGroups = item.groupsAuthors
                .split(", ")
                .map((group): string => group.trim().toLowerCase());

              return groupsAuthors.some((group): boolean =>
                itemGroups.includes(group),
              );
            }

            return itemValue.includes(filterValue);
          }),
        ),
      );
    },
    [dataset, setFilters],
  );

  const { Filters, options, removeFilter } = useFilters({
    dataset,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options: optionsFilters,
    setFilteredDataset: setFilters,
    setValues: handleSetValues,
  });

  const filteredData = useMemo(
    (): IOrganizationAuthorsTable[] =>
      filters.filter((item): boolean =>
        item.actorName.toLowerCase().includes(search.toLowerCase()),
      ),
    [filters, search],
  );
  const { table } = useTable({
    columns: tableColumns,
    data: filteredData,
    id: "billing-authors-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      paginationMode: "client-side",
    },
    total: filteredData.length,
  });

  return (
    <div>
      <Heading fontWeight={"bold"} mb={2} mt={2} size={"md"}>
        {t("organization.tabs.billing.authors.title")}
      </Heading>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
            <Container id={"actions-container"}>
              <Table.Export
                csvConfig={{
                  export: true,
                  headers: {
                    groupsAuthors: t(
                      "organization.tabs.billing.authors.headers.activeGroups",
                    ),
                  },
                  name: t("organization.tabs.billing.authors.title"),
                }}
                data={filteredData}
              >
                <Button
                  icon={"file-export"}
                  id={"CSVExportBtn"}
                  tooltip={t("buttons.exportTip", { format: "CSV" })}
                  variant={"ghost"}
                >
                  {t("group.findings.exportCsv.text")}
                </Button>
              </Table.Export>
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
    </div>
  );
};
